import './style.css';
import { APP_VERSION, ADMIN_QUERY_PARAM } from './config.js';
import { isPaired, getDeviceMeta } from './state/device-store.js';
import { logInfo, logError, logWarn } from './state/log-store.js';
import { renderPairingUI } from './pairing/ui.js';
import { startPolling, syncProgramOnce } from './sync/poller.js';
import { startRotation, showBlackScreen } from './player/rotation.js';
import {
  enqueueTelemetry,
  drainTelemetryQueue,
  attachOnlineDrainListener,
} from './api/telemetry-queue.js';
import { HEARTBEAT_INTERVAL_MS } from './config.js';
import { getProgram, subscribeProgram } from './state/program-store.js';
import { startGestureWatcher } from './dashboard/gesture.js';
import {
  applyManifestPlayback,
  guardBootPlaybackFromCache,
} from './sync/apply-manifest-playback.js';
import { resolvePlatform, setPlatform, getPlatform } from './platform/registry.js';
import { applyDisplayProfile } from './player/display-viewport.js';
import { startConnectionStatus } from './ui/connection-status.js';
import { shouldRotate } from './state/playback-status-store.js';

const playerEl = document.getElementById('player');

async function registerSW() {
  if (!('serviceWorker' in navigator)) return;

  // Vite dev serves /src and /@vite from the dev server — they cannot load offline on refresh.
  if (import.meta.env.DEV) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        await reg.unregister();
      }
      if (regs.length) {
        logInfo('Unregistered service worker (Vite dev — refresh offline non supportato)');
      }
      logInfo('Per refresh offline: pnpm --filter @signedge/player-web offline (build + preview)');
    } catch (err) {
      logWarn('Could not unregister service worker in dev', { error: err.message });
    }
    return;
  }

  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    logInfo('SW registered', { scope: reg.scope });
  } catch (err) {
    logError('SW registration failed', { error: err.message });
  }
}

function sendHeartbeat(deviceId) {
  const platform = getPlatform();
  // Ping di liveness: l'API aggiorna solo lastSeenAt. Se il device rientra da
  // offline, questo stesso ping fa registrare al server la transizione 'online'.
  return enqueueTelemetry(deviceId, {
    eventType: 'heartbeat',
    payload: {
      status: navigator.onLine ? 'online' : 'offline',
      appVersion: APP_VERSION,
      platformId: platform.id,
      capabilities: platform.capabilities,
    },
  });
}

function startHeartbeat() {
  const meta = getDeviceMeta();
  if (!meta?.deviceId) return;
  // Drena eventuali eventi accumulati offline non appena la rete torna.
  attachOnlineDrainListener();

  // Al ritorno della connessione: ping immediato per segnare il rientro
  // (transizione 'online') senza aspettare il prossimo tick da 5 min.
  window.addEventListener('online', () => {
    void sendHeartbeat(meta.deviceId);
  });

  setInterval(async () => {
    // Tenta drain prima del nuovo ping — preserva l'ordine cronologico
    // degli eventi in caso il device sia stato offline per un po'.
    try {
      await drainTelemetryQueue();
    } catch (_err) {
      // niente di fatale, riproviamo al prossimo tick
    }
    // Ping di liveness corrente: se offline o se fallisce, finisce in coda.
    await sendHeartbeat(meta.deviceId);
  }, HEARTBEAT_INTERVAL_MS);
}

async function boot() {
  startConnectionStatus();
  logInfo(`Booting Signedge Player v${APP_VERSION}`);

  // Admin panel?
  const params = new URLSearchParams(window.location.search);
  const adminMode = params.get(ADMIN_QUERY_PARAM) === '1';

  setPlatform(await resolvePlatform());
  logInfo('Platform resolved', { platformId: getPlatform().id });

  applyDisplayProfile({
    orientation: 'landscape',
    screenResolution: { width: 1920, height: 1080 },
  });

  if (!isPaired()) {
    renderPairingUI(playerEl);
    return;
  }

  await registerSW();

  // Wire program updates → rotation
  subscribeProgram(({ program, programChanged }) => {
    if (program?.displayProfile) {
      applyDisplayProfile(program.displayProfile);
    }
    if (shouldRotate(program)) {
      if (programChanged) {
        startRotation(playerEl);
      }
    } else {
      showBlackScreen(playerEl);
    }
  });

  let bootSyncedFresh = false;
  try {
    if (navigator.onLine) {
      await guardBootPlaybackFromCache(playerEl);
      await syncProgramOnce();
      bootSyncedFresh = true;
    } else {
      await applyManifestPlayback(playerEl);
    }
  } catch (err) {
    logError('Failed to sync program on boot', { error: err.message });
  }

  // Skip the immediate tick when boot already fetched fresh — avoids a duplicate
  // GET /player/current-program right after startup.
  startPolling({ immediate: !bootSyncedFresh });
  // Start telemetry
  startHeartbeat();

  if (adminMode) {
    await mountDashboardIfAvailable();
  }

  startGestureWatcher(() => mountDashboardIfAvailable());
}

async function mountDashboardIfAvailable() {
  try {
    const { mountDashboard } = await import('./dashboard/panel.js');
    mountDashboard();
  } catch (err) {
    logWarn('Dashboard unavailable (offline or uncached)', { error: err.message });
  }
}

boot().catch(async (err) => {
  logError('Boot failed', { error: err.message });

  if (getProgram()?.slots?.length) {
    logWarn('Boot error after program loaded; continuing playback');
    startPolling();
    return;
  }

  if (isPaired()) {
    try {
      if (await applyManifestPlayback(playerEl)) {
        logInfo('Offline boot recovery from IndexedDB');
        startPolling();
        startGestureWatcher(() => mountDashboardIfAvailable());
        return;
      }
    } catch (recoveryErr) {
      logError('Offline boot recovery failed', { error: recoveryErr.message });
    }
  }

  playerEl.innerHTML = `<div style="color:#fff;padding:24px;">Boot error: ${err.message}</div>`;
});
